/**
 * Utility service for handling Organizational Unit (OU) path operations
 * Provides static utility methods for parsing, extracting, and manipulating OU paths
 * used in tree-structured reports and hierarchical data displays
 *
 * All methods are static for easy use in both DI and static contexts
 */
export class OuPathService {
  /**
   * Converts an OU path to an array of path segments for AG Grid's tree data
   * First segment has no leading slash, subsequent segments have leading slash
   *
   * @param ouPath - The OU path string (e.g., "/Engineering/Development")
   * @returns Array of path segments (e.g., ["Engineering", "/Development"])
   *
   * @example
   * OuPathService.parsePathForGrid("/Engineering/Development") // ["Engineering", "/Development"]
   * OuPathService.parsePathForGrid("/IT") // ["IT"]
   */
  static parsePathForGrid(ouPath: string): string[] {
    const segments = ouPath
      .split('/')
      .filter((segment) => segment.length > 0)
      .map((segment, index) => (index === 0 ? segment : `/${segment}`));
    // Root OU '/' produces no segments — represent as ['/'] for AG Grid tree placement
    if (segments.length === 0 && ouPath === '/') {
      return ['/'];
    }
    return segments;
  }

  /**
   * Extracts all parent paths from a given OU path
   *
   * @param ouPath - The OU path string (e.g., "/A/B/C")
   * @returns Array of parent paths (e.g., ["/A", "/A/B"])
   *
   * @example
   * OuPathService.extractParentPaths("/A/B/C") // ["/A", "/A/B"]
   * OuPathService.extractParentPaths("/A") // []
   */
  static extractParentPaths(ouPath: string): string[] {
    const segments = ouPath.split('/').filter((s) => s.length > 0);
    const parentPaths: string[] = [];

    for (let i = 1; i < segments.length; i++) {
      parentPaths.push(`/${segments.slice(0, i).join('/')}`);
    }

    return parentPaths;
  }

  /**
   * Generates all unique parent paths from an array of OU paths
   *
   * @param ouPaths - Array of OU path strings
   * @returns Set of all unique parent paths
   *
   * @example
   * OuPathService.getAllParentPaths(["/A/B/C", "/A/D"]) // Set(["/A", "/A/B"])
   */
  static getAllParentPaths(ouPaths: string[]): Set<string> {
    const allParents = new Set<string>();

    ouPaths.forEach((ouPath) => {
      const parents = OuPathService.extractParentPaths(ouPath);
      parents.forEach((parent) => allParents.add(parent));
    });

    return allParents;
  }

  /**
   * Checks if an OU path is a leaf node (has no children) in a dataset
   *
   * @param ouPath - The OU path to check
   * @param allPaths - Array of all OU paths in the dataset
   * @returns True if the path is a leaf node (no children)
   *
   * @example
   * OuPathService.isLeafNode("/A/B", ["/A/B", "/A/B/C"]) // false (has child "/A/B/C")
   * OuPathService.isLeafNode("/A/B/C", ["/A/B", "/A/B/C"]) // true (no children)
   */
  static isLeafNode(ouPath: string, allPaths: string[]): boolean {
    return !allPaths.some((otherPath) => {
      if (ouPath === '/') {
        return otherPath.startsWith('/') && otherPath.length > 1;
      }
      return otherPath.startsWith(`${ouPath}/`);
    });
  }

  /**
   * Filters an array of items to only leaf nodes
   *
   * @param items - Array of objects with ouPath property
   * @returns Array containing only items that are leaf nodes
   *
   * @example
   * OuPathService.getLeafNodes([{ouPath: "/A"}, {ouPath: "/A/B"}]) // [{ouPath: "/A/B"}]
   */
  static getLeafNodes<T extends { ouPath: string }>(items: T[]): T[] {
    return items.filter((item) => {
      const itemPath = item.ouPath || '';
      const hasChildren = items.some((otherItem) => {
        const otherPath = otherItem.ouPath || '';
        if (itemPath === '/') {
          return (
            otherItem !== item &&
            otherPath.startsWith('/') &&
            otherPath.length > 1
          );
        }
        return otherItem !== item && otherPath.startsWith(`${itemPath}/`);
      });
      return !hasChildren;
    });
  }

  /**
   * Finds all descendant paths for a given parent path
   *
   * @param parentPath - The parent OU path
   * @param allPaths - Array of all OU paths in the dataset
   * @returns Array of paths that are descendants of the parent
   *
   * @example
   * OuPathService.getDescendants("/A", ["/A/B", "/A/B/C", "/D"]) // ["/A/B", "/A/B/C"]
   */
  static getDescendants(parentPath: string, allPaths: string[]): string[] {
    if (parentPath === '/') {
      return allPaths.filter((path) => path.startsWith('/') && path.length > 1);
    }
    return allPaths.filter((path) => path.startsWith(`${parentPath}/`));
  }

  /**
   * Finds all descendant items for a given parent path
   *
   * @param parentPath - The parent OU path
   * @param items - Array of objects with ouPath property
   * @returns Array of items that are descendants of the parent
   *
   * @example
   * OuPathService.getDescendantItems("/A", [{ouPath: "/A/B"}, {ouPath: "/C"}]) // [{ouPath: "/A/B"}]
   */
  /**
   * Redistributes non-leaf OU data for correct AG Grid tree aggregation.
   * When an OU has both direct devices and child OUs, AG Grid's aggFunc
   * replaces the parent's own values with aggregated child sums, losing
   * the parent's direct devices. This method renames such parent OUs to
   * "{ouPath}/(Direct)" so they become leaf nodes under an auto-created group.
   *
   * The suffix is numbered if a real OU of that name already exists, because
   * two rows sharing a path collapse into one tree node and one silently wins.
   */
  static redistributeNonLeafData<T extends Record<string, unknown>>(
    items: T[],
    numericFields: string[],
    pathField = 'ouPath'
  ): T[] {
    const paths = items
      .map((item) => item[pathField])
      .filter((path): path is string => typeof path === 'string');
    const taken = new Set(paths);

    return items.map((item) => {
      // A row can carry no path at all — Model/AUE injects zero-count buckets
      // with an undefined OU so every timeframe still appears in the tree.
      const ouPath = item[pathField];
      if (typeof ouPath !== 'string') return item;

      const hasChildren =
        OuPathService.getDescendants(ouPath, paths).length > 0;
      if (!hasChildren) return item;

      const hasData = numericFields.some((f) => Number(item[f]) > 0);
      if (!hasData) return item;

      // The synthesized path has to be free AND childless: an existing
      // "/A/(Direct)/X" would make "/A/(Direct)" a non-leaf, so AG Grid would
      // overwrite it with its child's sum — the very bug this method prevents.
      const base = ouPath === '/' ? '' : ouPath;
      let directPath = `${base}/(Direct)`;
      for (
        let n = 2;
        taken.has(directPath) ||
        OuPathService.getDescendants(directPath, paths).length > 0;
        n++
      ) {
        directPath = `${base}/(Direct ${n})`;
      }
      taken.add(directPath);
      return { ...item, [pathField]: directPath };
    });
  }

  static getDescendantItems<T extends { ouPath: string }>(
    parentPath: string,
    items: T[]
  ): T[] {
    if (parentPath === '/') {
      return items.filter((item) => {
        const p = item.ouPath || '';
        return p.startsWith('/') && p.length > 1;
      });
    }
    return items.filter((item) =>
      (item.ouPath || '').startsWith(`${parentPath}/`)
    );
  }
}
